import React, { useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const STAR = "M12 0c.9 6.8 4.3 10.2 12 12-7.7 1.8-11.1 5.2-12 12-.9-6.8-4.3-10.2-12-12C7.7 10.2 11.1 6.8 12 0Z";
const VIEWPORT = { once: true, margin: "-40px" };

export function CircledWord({ children, color, show, delay = 0, strokeWidth = 2, mount = false, testId = "circled-word" }) {
  const ref = useRef(null);
  const [dim, setDim] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      setDim((d) => (d && d.w === w && d.h === h ? d : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const controlled = show !== undefined;
  let svg = null;

  if (dim && dim.w > 4 && dim.h > 4) {
    const { w, h } = dim;
    const cx = w / 2;
    const cy = h / 2;
    const rx = w / 2 + Math.max(6, w * 0.035);
    const ry = h / 2 + Math.max(5, h * 0.16);
    const a = -0.55;
    const sx = cx + rx * Math.cos(a);
    const sy = cy + ry * Math.sin(a);
    const ex = cx - rx * Math.cos(a);
    const ey = cy - ry * Math.sin(a);
    const d = `M ${sx} ${sy} A ${rx} ${ry} 0 1 0 ${ex} ${ey} A ${rx} ${ry} 0 1 0 ${sx} ${sy}`;
    const starSize = Math.min(34, Math.max(13, h * 0.26));
    const starX = cx + rx * Math.cos(a);
    const starY = cy + ry * Math.sin(a);

    const drawDur = mount ? 1.1 : 0.9;
    const pathMotion = controlled
      ? { initial: false, animate: show ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }, transition: { duration: 0.9, ease: "easeInOut" } }
      : mount
        ? { initial: { pathLength: 0 }, animate: { pathLength: 1 }, transition: { delay, duration: drawDur, ease: "easeInOut" } }
        : { initial: { pathLength: 0 }, whileInView: { pathLength: 1 }, viewport: VIEWPORT, transition: { delay, duration: drawDur, ease: "easeInOut" } };

    const starDelay = delay + drawDur - 0.15;
    const starMotion = controlled
      ? { initial: false, animate: show ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }, transition: { delay: show ? 0.75 : 0, duration: 0.35, ease: "backOut" } }
      : mount
        ? { initial: { scale: 0, opacity: 0 }, animate: { scale: 1, opacity: 1 }, transition: { delay: starDelay, duration: 0.35, ease: "backOut" } }
        : { initial: { scale: 0, opacity: 0 }, whileInView: { scale: 1, opacity: 1 }, viewport: VIEWPORT, transition: { delay: starDelay, duration: 0.35, ease: "backOut" } };

    svg = (
      <svg
        className="absolute inset-0 pointer-events-none overflow-visible"
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        fill="none"
        aria-hidden="true"
      >
        <g transform={`rotate(-4 ${cx} ${cy})`}>
          <motion.path d={d} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" {...pathMotion} />
          <motion.g style={{ transformBox: "fill-box", transformOrigin: "center" }} {...starMotion}>
            <path
              d={STAR}
              fill={color}
              transform={`translate(${starX - starSize / 2} ${starY - starSize / 2}) scale(${starSize / 24})`}
            />
          </motion.g>
        </g>
      </svg>
    );
  }

  return (
    <span ref={ref} className="relative inline-block whitespace-nowrap" style={{ color }} data-testid={testId}>
      {children}
      {svg}
    </span>
  );
}
